import React from "react";
import { Heading } from "../../design/heading";
import EmptyStateView from "../../components/EmptyStateView";
import ErrorView from "../../components/ErrorView";
import LoadingView from "../../components/LoadingView";
import { useMatter } from "../../contexts/MatterContext";
import { useFinancialItem } from "../../contexts/FinancialItemContext";
import { financialItemTypes } from "../../models/FinancialItemType";
import FinancialItemListSection from "./FinancialListSection";
import FinancialStatsSummary from "./FinancialStatsSummary";
import { PropertyDivisionUpsellCard } from "./components/PropertyDivisionUpsellCard";
import { useCreateFinancialItemDialog } from "../../providers/CreateFinancialItemDialogProvider/CreateFinancialItemDialogProvider";
import { Button } from "../../design/button";
import { PlusIcon } from "@heroicons/react/16/solid";

const DebtListPage: React.FC = () => {
  const { openDialog } = useCreateFinancialItemDialog();
  const { currentMatter } = useMatter();
  const { financialItems, loading, error } = useFinancialItem();

  if (loading.list) {
    return <LoadingView title="Loading Assets & Debts..." />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const filteredFinancialItems = financialItems.filter(
    (item) => item.category === "liability",
  );

  const groupedFinancialItems = financialItemTypes.reduce(
    (acc: Record<string, typeof financialItems>, type) => {
      acc[type.id] = filteredFinancialItems.filter(
        (item) => item.type === type.id,
      );
      return acc;
    },
    {} as Record<string, typeof financialItems>,
  );

  return (
    <div>
      {!currentMatter?.unlockedFeatures?.includes("financial_disclosures") && (
        <PropertyDivisionUpsellCard />
      )}
      {currentMatter?.unlockedFeatures?.includes("financial_disclosures") && (
        <div className="space-y-4">
          <div className="flex items-end justify-between gap-4 mb-6">
            <Heading className="text-2xl font-bold">Assets & Debts</Heading>
            <div className="flex space-x-4">
              <Button onClick={() => openDialog("asset")}>
                <PlusIcon className="w-4 h-4" />
                <span className="hidden sm:block">Add Debt</span>
              </Button>
            </div>
          </div>

          {filteredFinancialItems.length === 0 && (
            <EmptyStateView
              title="No Financial Items"
              message="No financial items found. Create some to get started."
              action={
                <Button onClick={() => openDialog("asset")}>
                  <PlusIcon className="w-4 h-4" />
                  <span className="hidden sm:block">Add Asset</span>
                </Button>
              }
            />
          )}
          {financialItemTypes.map((type) => (
            <FinancialItemListSection
              key={type.id}
              type={type}
              items={groupedFinancialItems[type.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DebtListPage;
